import React, { useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { ClientDto } from 'api/Clients/Types/Client'
import { ClientRestrictionId } from 'api/Clients/Types/ClientRestrictions'
import { CreateBonusTransaction } from 'api/Transaction/Types/CreateBonusTransaction'
import { RawTransactionDto, RawTransactionFilterKey } from 'api/Transaction/Types/RawTransactionDto'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { Loading } from 'components/Loading'
import { errorToast, ToastContext } from 'components/Toast/Toast'
import { isEmpty } from 'lodash'
import { useAppDispatch, useAppSelector } from 'store'
import { initialState } from 'store/grid'
import { fetchBonusTransactionTypeAction } from 'store/transactions/transaction.action'
import { fetchBonusLabelsAction } from 'store/wallet/wallet-actions'
import { getIdFromLocationHeader } from 'utils/api'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { formatLocalDateUTC } from 'utils/date'
import { FilterLogic } from 'utils/QueryHelpers'

import { useApiClient } from '../../../../utils/api/ApiClient'
import { ClientsApiClient } from '../../../../utils/api/clientsApi'
import { WalletsApiClient } from '../../../../utils/api/walletsApiClient'
import { SelectMultipleTransactions } from '../../SelectMultipleTransactions'
import { BonusForm } from './BonusForm'

interface Props {
  client: ClientDto | null
  preSelectedWallet?: WalletDto
  setClient(): void
}

const BonusPage: React.FC<Props> = ({ client, preSelectedWallet, setClient }) => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)
  const [currency, setCurrency] = useState<
    null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  >(null)
  const [walletId, setWalletId] = useState<string>('')
  const [selectedTransactions, setSelectedTransactions] = useState<RawTransactionDto[]>([])
  const [isTransactionModalOpen, setTransactionModalOpen] = useState(false)
  const [confirmationData, setConfirmationData] = useState<CreateBonusTransaction>()
  const [isConfirmationModalOpen, setConfirmationModal] = useState<boolean>(false)

  const dispatch = useAppDispatch()

  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const bonusTypes = useAppSelector((state) => state.transactions.bonusTypes)
  const bonusLabels = useAppSelector((state) => state.wallet.bonusLabels)

  const dateFormatType = auth?.user.dateFormatType.name
  const [transaction] = confirmationData?.transactions || []

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (!client) {
      return undefined
    }
    return walletsApiClient.getClientWallet(client.id)
  }, [client]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEmpty(bonusLabels)) dispatch(fetchBonusLabelsAction(walletsApiClient))
    dispatch(fetchBonusTransactionTypeAction(walletsApiClient))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (preSelectedWallet?.id) setWalletId(preSelectedWallet.id)
  }, [preSelectedWallet?.id])

  const { data: wallets, isLoading } = useFetchOne(fetchData)

  const onSetClient = () => {
    setClient()
  }

  const setWallet = async (selectedWalletId: string) => {
    const walletDto = (wallets || []).find((x) => x.id === selectedWalletId)
    if (walletDto) {
      setWalletId(walletDto.id)
      setCurrency(walletDto.currency.id)
    }
  }

  const onSubmit = async (values: CreateBonusTransaction) => {
    if (client) {
      const clientDetailed = await clientsApiClient.getClient(client.id)
      const hasExcludeFromBonusRestriction = clientDetailed.restrictions.some(
        (restriction) => restriction.id === ClientRestrictionId.ExcludeFromBonusCampaign
      )
      if (hasExcludeFromBonusRestriction) {
        setToast(errorToast('Client is restricted from getting bonuses'))
        return
      }
    }

    setConfirmationData(values)
    setConfirmationModal(true)
  }

  const handleConfirmationSubmit = async () => {
    if (confirmationData) {
      try {
        const response = await walletsApiClient.createBonusTransactions(confirmationData)
        setToast({
          type: 'success',
          requireInteraction: true,
          title: 'Bonus has been submitted.',
          subtitle: 'Click for details',
          url: generatePath('/transactions/:id', {
            id: getIdFromLocationHeader(response.headers.location),
          }),
        })
        forceRefresh()
        setConfirmationModal(false)
        resetAll()
      } catch (e) {
        console.error(e)
        setConfirmationModal(false)
      }
    }
  }

  const handleConfirmationCloseModal = () => {
    setConfirmationModal(false)
  }

  const getSelectedTransactions = (transactions: RawTransactionDto[]) => {
    setSelectedTransactions(transactions)
    setTransactionModalOpen(false)
  }

  const onSelectTransactionModal = () => {
    if (walletId) {
      setTransactionModalOpen(true)
    }
  }

  const calculateDepositTransactionsAmount = () => {
    let amount = 0
    selectedTransactions.forEach((x) => (amount += x.amount))
    return amount
  }

  const selectWalletName = () => {
    return wallets?.find((x) => x.id === transaction?.walletId)?.name
  }

  const selectBonusTypeName = () => {
    return bonusTypes?.find((x) => x.id === transaction?.bonusTypeId)?.name
  }

  const resetAll = () => {
    setCurrency(null)
    setWalletId('')
    setSelectedTransactions([])
  }

  return (
    <Loading isLoading={isLoading}>
      {isTransactionModalOpen && walletId && (
        <Modal
          width={1300}
          visible={isTransactionModalOpen}
          onClose={() => setTransactionModalOpen(false)}
        >
          <SelectMultipleTransactions
            selectedTransactions={selectedTransactions}
            getTransactions={getSelectedTransactions}
            walletId={walletId}
            query={createBonusTransactionsQuery()}
          />
        </Modal>
      )}
      {isConfirmationModalOpen && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleConfirmationSubmit}
          onCancel={handleConfirmationCloseModal}
          visible={isConfirmationModalOpen}
          renderTitle={
            <div>
              <p>
                You are going to add a bonus for the wallet ({selectWalletName()}). The parameters
                selected are:
              </p>
              <div>
                <div id='field'>
                  <strong>Client:</strong> {client?.name}
                </div>
                <div id='field'>
                  <strong>Wallet:</strong> {selectWalletName()}
                </div>
                <div id='field'>
                  <strong>Bonus Type:</strong> {selectBonusTypeName()}
                </div>
                <div id='field'>
                  <strong>Amount:</strong> {transaction?.amount} {currency}
                </div>
                <div id='field'>
                  <strong>Lots required:</strong> {transaction?.lotsRequired}
                </div>
                <div id='field'>
                  <strong>Expiry:</strong> {formatLocalDateUTC(transaction?.expiryDate)}
                </div>
              </div>
            </div>
          }
        />
      )}
      <BonusForm
        bonusTypes={bonusTypes}
        onSelectTransactionModal={onSelectTransactionModal}
        depositTransactionsAmount={calculateDepositTransactionsAmount()}
        walletId={walletId}
        setWallet={setWallet}
        client={client}
        setClient={onSetClient}
        wallets={wallets || []}
        transactionIds={selectedTransactions.map((x) => x.id)}
        selectedTransactions={selectedTransactions}
        removeSelectedTransactions={() => setSelectedTransactions([])}
        onSubmit={onSubmit}
        dateFormatType={dateFormatType}
        bonusLabels={bonusLabels}
      />
    </Loading>
  )
}

const createBonusTransactionsQuery = () => {
  const initialGridState = initialState()

  return {
    search: {
      ...initialGridState.filter,
      logic: initialGridState.filter.logic,
      defaultFilters: {
        logic: FilterLogic.OR,
        filters: [
          {
            field: RawTransactionFilterKey.type,
            operator: '==',
            value: 200,
          },
          {
            field: RawTransactionFilterKey.type,
            operator: '==',
            value: 112,
          },
          {
            logic: FilterLogic.AND,
            filters: [
              {
                field: RawTransactionFilterKey.state,
                operator: '==',
                value: 200,
              },
            ],
          },
        ],
      },
    },
  }
}

export default BonusPage
